'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { createOnrampClient, type DirectPurchaseCheckout, directPurchaseEntryScope, fundingNavigationManifest, OnrampApiError, type OnrampClient } from '@morfi/onramp-sdk';
import { useConnectedExchanges, useCryptoDepositCapability, useCryptoDepositSourcePrefetch, useFundingNavigation, useOnrampSession, usePendingFundingOperation, useSavedFundingMethods } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { toSdkApiBase } from '@/lib/network/sdk-api-base';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { logger } from '@/lib/telemetry/logger';
import { useFundingDevtoolsStore } from '@/stores/funding-devtools';
import { FundingExchange } from './checkout/exchange/funding-exchange';
import { FundingCheckout } from './checkout/funding-checkout';
import { FundingMethodPicker } from './checkout/funding-method-picker';
import { FundingConnectedWallet } from './connected-wallet/funding-connected-wallet';
import { FundingCryptoDeposit } from './crypto-deposit/funding-crypto-deposit';
import { FundingHistory } from './history/funding-history';
import { FundingReceipt } from './history/funding-receipt';
import { FundingManualTransfer } from './manual-transfer/funding-manual-transfer';
import { WithdrawCheckout } from './offramp/withdraw-checkout';
import { FundingChromeContext } from './shared/funding-chrome';
import { FundingDialog } from './shared/funding-dialog';
import { FundingEntryFeedback } from './shared/funding-entry-feedback';
import { useFundingEntryProfile } from './shared/funding-entry-profile';
import { FundingEntryWarmup } from './shared/funding-entry-warmup';
import { FundingLoading } from './shared/funding-loading';
import { useFundingRealtime } from './shared/funding-realtime';
import { fundingTelemetry } from './shared/funding-telemetry';
import { useFundingEntryAuthentication } from './shared/use-funding-entry-authentication';
import { useFundingResource } from './shared/use-funding-resource';
export function FundingEntry(t0) {
  const $ = _c(12);
  const {
    open: t1,
    prewarm: t2,
    mode: t3,
    onClose,
    directPurchase
  } = t0;
  const open = t1 === undefined ? true : t1;
  const prewarm = t2 === undefined ? false : t2;
  const mode = t3 === undefined ? "deposit" : t3;
  const config = useNetworkConfig();
  const {
    user,
    isAuthenticated
  } = useAuth();
  if (!isAuthenticated || !user) {
    return null;
  }
  const t4 = `${user.id}:${config.apiUrl}:${config.apiKey ?? ""}`;
  let t5;
  if ($[0] !== directPurchase) {
    t5 = directPurchase ? {
      directPurchase
    } : {};
    $[0] = directPurchase;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== config.apiKey || $[3] !== config.apiUrl || $[4] !== mode || $[5] !== onClose || $[6] !== open || $[7] !== prewarm || $[8] !== t4 || $[9] !== t5 || $[10] !== user.id) {
    t6 = <FundingClient key={t4} open={open} prewarm={prewarm} mode={mode} {...t5} owner={user.id} apiUrl={config.apiUrl} apiKey={config.apiKey} onClose={onClose} />;
    $[2] = config.apiKey;
    $[3] = config.apiUrl;
    $[4] = mode;
    $[5] = onClose;
    $[6] = open;
    $[7] = prewarm;
    $[8] = t4;
    $[9] = t5;
    $[10] = user.id;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function FundingClient(t0) {
  const $ = _c(13);
  const {
    open,
    prewarm,
    mode,
    owner,
    apiUrl,
    apiKey,
    onClose,
    directPurchase
  } = t0;
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileVisited, setProfileVisited] = useState(false);
  let t1;
  if ($[0] !== apiKey || $[1] !== apiUrl || $[2] !== owner) {
    t1 = () => createOnrampClient({
      telemetry: fundingTelemetry,
      progressKey: `${owner}:${apiUrl}`,
      baseUrl: process.env.NODE_ENV === "development" ? window.location.origin : toSdkApiBase(apiUrl),
      getAuthHeaders: async () => ({
        "X-API-Key": apiKey ?? "",
        "X-Funding-Location-Override": useFundingDevtoolsStore.getState().preferences[owner] === false ? "off" : "on"
      })
    });
    $[0] = apiKey;
    $[1] = apiUrl;
    $[2] = owner;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const client = useFundingResource(t1);
  let t2;
  if ($[4] !== client || $[5] !== directPurchase || $[6] !== mode || $[7] !== onClose || $[8] !== open || $[9] !== prewarm || $[10] !== profileOpen || $[11] !== profileVisited) {
    t2 = client ? <FundingEntryWarmup client={client} open={open} prewarm={prewarm} mode={mode}>{mode === "withdraw" && <WithdrawCheckout client={client} open={open && !profileOpen} onClose={onClose} onProfile={() => {
        setProfileVisited(true);
        setProfileOpen(true);
      }} />}{(mode !== "withdraw" || profileVisited) && <FundingEntryBody key={client.cacheKey} client={client} open={open && (mode !== "withdraw" || profileOpen)} profileOnly={mode === "withdraw" || mode === "profile"} onClose={mode === "withdraw" ? () => setProfileOpen(false) : onClose} {...directPurchase ? {
        directPurchase
      } : {}} />}</FundingEntryWarmup> : null;
    $[4] = client;
    $[5] = directPurchase;
    $[6] = mode;
    $[7] = onClose;
    $[8] = open;
    $[9] = prewarm;
    $[10] = profileOpen;
    $[11] = profileVisited;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
function FundingEntryBody(props) {
  const $ = _c(15);
  let t0;
  if ($[0] !== props.directPurchase) {
    t0 = props.directPurchase ? {
      ...props.directPurchase.purchase,
      preferredMethod: props.directPurchase.preferredMethod
    } : undefined;
    $[0] = props.directPurchase;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const navigation = useFundingNavigation(props.client, "deposit", t0);
  const visited = useRef(false);
  let t1;
  if ($[2] !== navigation || $[3] !== props.directPurchase || $[4] !== props.open || $[5] !== props.profileOnly) {
    t1 = () => {
      if (props.open) {
        if (props.profileOnly) {
          if (!visited.current) {
            navigation.deposit();
          }
        } else {
          navigation.restore(visited.current || Boolean(props.directPurchase && fundingNavigationManifest.direct_token_purchase.close === "preserve"));
        }
        visited.current = true;
      }
    };
    $[2] = navigation;
    $[3] = props.directPurchase;
    $[4] = props.open;
    $[5] = props.profileOnly;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== navigation.deposit || $[8] !== navigation.restore || $[9] !== props.directPurchase || $[10] !== props.open || $[11] !== props.profileOnly) {
    t2 = [props.open, props.directPurchase, props.profileOnly, navigation.deposit, navigation.restore];
    $[7] = navigation.deposit;
    $[8] = navigation.restore;
    $[9] = props.directPurchase;
    $[10] = props.open;
    $[11] = props.profileOnly;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  useEffect(t1, t2);
  let t3;
  if ($[13] !== props) {
    t3 = <FundingEntryVisit {...props} />;
    $[13] = props;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
function FundingEntryVisit(t0) {
  const $ = _c(92);
  const {
    open,
    client,
    onClose,
    profileOnly: t1,
    directPurchase
  } = t0;
  const profileOnly = t1 === undefined ? false : t1;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const t2 = profileOnly ? "profile_title" : "deposit_title";
  let t3;
  if ($[0] !== t || $[1] !== t2) {
    t3 = t(t2);
    $[0] = t;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    chrome,
    profileReady,
    openProfile
  } = useFundingEntryProfile(open, profileOnly, t3, onClose);
  const [checkoutContainer, setCheckoutContainer] = useState(null);
  const [sessionGeneration, setSessionGeneration] = useState(0);
  const [manualExchangeOpen, setManualExchangeOpen] = useState(false);
  const [dismissedExchangeId, setDismissedExchangeId] = useState(null);
  const [dismissedCryptoDepositId, setDismissedCryptoDepositId] = useState(null);
  const [selectedCryptoDepositId, setSelectedCryptoDepositId] = useState(null);
  const [dismissedConnectedWallet, setDismissedConnectedWallet] = useState(null);
  const [selectedConnectedWallet, setSelectedConnectedWallet] = useState(null);
  const cryptoDeposit = useCryptoDepositCapability(client, "web");
  useSavedFundingMethods(client);
  const exchanges = useConnectedExchanges(client, "web");
  const realtime = useFundingRealtime(client, open);
  let t4;
  if ($[3] !== directPurchase) {
    t4 = directPurchase ? {
      ...directPurchase.purchase,
      preferredMethod: directPurchase.preferredMethod
    } : undefined;
    $[3] = directPurchase;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const navigation = useFundingNavigation(client, "deposit", t4);
  const t5 = open && navigation.page === "crypto_deposit" && !profileOnly && !directPurchase && cryptoDeposit.data === true;
  let t6;
  if ($[5] !== locale || $[6] !== t5) {
    t6 = {
      surface: "web",
      locale,
      enabled: t5
    };
    $[5] = locale;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useCryptoDepositSourcePrefetch(client, t6);
  const [exchangeRevision, setExchangeRevision] = useState(navigation.revision);
  const historyOpen = navigation.page === "history" || navigation.page === "receipt" && navigation.receiptOrigin === "history";
  const receiptId = navigation.page === "receipt" && navigation.receiptOrigin === "deposit" ? navigation.receiptId : null;
  const t7 = navigation.page === "deposit";
  let t8;
  if ($[8] !== sessionGeneration || $[9] !== t7) {
    t8 = {
      enabled: t7,
      navigationKey: sessionGeneration
    };
    $[8] = sessionGeneration;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const session = useOnrampSession(client, t8);
  const pending = usePendingFundingOperation(client, navigation.page === "deposit", navigation.revision);
  let t10;
  let t9;
  if ($[11] !== pending.error) {
    t9 = () => {
      if (pending.error) {
        logger.warn("onramp.pending_recovery_failed");
      }
    };
    t10 = [pending.error];
    $[11] = pending.error;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  const pendingExchangeId = pending.data?.method === "cex_connect" ? pending.data.id : null;
  const pendingCryptoDepositId = pending.data?.method === "crypto_deposit" ? pending.data.id : null;
  const pendingConnectedWalletId = pending.data?.method === "wallet_connect" && pending.data.status !== "created" ? pending.data.id : null;
  const activeCryptoDepositId = selectedCryptoDepositId ?? pendingCryptoDepositId;
  const cryptoDepositOpen = navigation.page === "crypto_deposit" || navigation.page === "deposit" && Boolean(activeCryptoDepositId && activeCryptoDepositId !== dismissedCryptoDepositId);
  const activeConnectedWalletId = selectedConnectedWallet?.revision === navigation.revision ? selectedConnectedWallet.id : pendingConnectedWalletId;
  const dismissedConnectedWalletId = dismissedConnectedWallet?.revision === navigation.revision ? dismissedConnectedWallet.id : null;
  const connectedWalletOpen = navigation.page === "connected_wallet" || navigation.page === "deposit" && Boolean(activeConnectedWalletId && activeConnectedWalletId !== dismissedConnectedWalletId);
  const exchangeOpen = navigation.page === "deposit" && navigation.revision === exchangeRevision && (manualExchangeOpen || Boolean(pendingExchangeId && pendingExchangeId !== dismissedExchangeId));
  const alternateFlow = exchangeOpen || cryptoDepositOpen || connectedWalletOpen || navigation.page === "manual_transfer";
  let t11;
  if ($[14] !== navigation.revision || $[15] !== pendingExchangeId) {
    t11 = function setExchangeOpen(openExchange) {
      if (openExchange) {
        setExchangeRevision(navigation.revision);
      }
      setManualExchangeOpen(openExchange);
      if (!openExchange) {
        setDismissedExchangeId(pendingExchangeId);
      }
    };
    $[14] = navigation.revision;
    $[15] = pendingExchangeId;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const setExchangeOpen = t11;
  const authentication = useFundingEntryAuthentication(client, navigation);
  const currentSession = authentication.session ?? session.data;
  let t12;
  let t13;
  if ($[17] !== authentication.error) {
    t12 = () => {
      if (authentication.error) {
        logger.warn("onramp.auth_session_failed");
      }
    };
    t13 = [authentication.error];
    $[17] = authentication.error;
    $[18] = t12;
    $[19] = t13;
  } else {
    t12 = $[18];
    t13 = $[19];
  }
  useEffect(t12, t13);
  const sessionRejected = session.error instanceof OnrampApiError && session.error.status === 401;
  const entryError = receiptId || currentSession && !sessionRejected ? null : session.error;
  const {
    openLogin
  } = useAuthGate();
  const expired = entryError instanceof OnrampApiError && entryError.status === 401;
  let t14;
  let t15;
  if ($[20] !== entryError) {
    t14 = () => {
      if (entryError) {
        logger.warn("onramp.session_failed", {
          code: entryError instanceof OnrampApiError ? entryError.code : "onramp_request_failed",
          requestId: entryError instanceof OnrampApiError ? entryError.requestId : undefined
        });
      }
    };
    t15 = [entryError];
    $[20] = entryError;
    $[21] = t14;
    $[22] = t15;
  } else {
    t14 = $[21];
    t15 = $[22];
  }
  useEffect(t14, t15);
  let t16;
  if ($[23] !== onClose || $[24] !== openLogin) {
    t16 = function signIn() {
      onClose();
      openLogin();
    };
    $[23] = onClose;
    $[24] = openLogin;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const signIn = t16;
  let t17;
  if ($[26] !== activeConnectedWalletId || $[27] !== activeCryptoDepositId || $[28] !== chrome || $[29] !== client || $[30] !== connectedWalletOpen || $[31] !== cryptoDepositOpen || $[32] !== currentSession || $[33] !== directPurchase || $[34] !== entryError || $[35] !== exchangeOpen || $[36] !== exchanges || $[37] !== expired || $[38] !== navigation || $[39] !== onClose || $[40] !== open || $[41] !== openProfile || $[42] !== pending.data || $[43] !== pending.isSuccess || $[44] !== pendingExchangeId || $[45] !== profileOnly || $[46] !== profileReady || $[47] !== realtime || $[48] !== receiptId || $[49] !== session || $[50] !== setExchangeOpen || $[51] !== signIn || $[52] !== t) {
    t17 = function renderDialog() {
      return <FundingDialog open={open} onClose={onClose} onHistory={() => navigation.openHistory()} onProfile={openProfile} chrome={profileOnly ? {
        ...chrome,
        picker: false,
        title: !profileReady ? t("profile_title") : chrome.title
      } : chrome} direct={navigation.page === "manual_transfer"}>{profileOnly && !profileReady ? chrome.picker ? <FundingLoading /> : <output>{t("profile_load_error")}</output> : null}<div className="rr-block flex flex-col gap-4" hidden={profileOnly && !profileReady}>{connectedWalletOpen ? <FundingConnectedWallet client={client} active={open} operationId={activeConnectedWalletId} onBack={() => {
            setDismissedConnectedWallet({
              id: activeConnectedWalletId,
              revision: navigation.revision
            });
            setSelectedConnectedWallet(null);
            navigation.back();
          }} onAddressDeposit={() => {
            setDismissedConnectedWallet({
              id: activeConnectedWalletId,
              revision: navigation.revision
            });
            setSelectedConnectedWallet(null);
            navigation.openCryptoDeposit();
          }} onComplete={id => {
            setDismissedConnectedWallet({
              id,
              revision: navigation.revision
            });
            setSelectedConnectedWallet(null);
            navigation.openReceipt(id, "deposit");
          }} /> : cryptoDepositOpen ? <FundingCryptoDeposit client={client} active={open} operationId={activeCryptoDepositId} onBack={() => {
            setDismissedCryptoDepositId(activeCryptoDepositId);
            setSelectedCryptoDepositId(null);
            navigation.back();
          }} onComplete={id_0 => {
            setDismissedCryptoDepositId(id_0);
            setSelectedCryptoDepositId(null);
            navigation.openReceipt(id_0, "deposit");
          }} /> : navigation.page === "manual_transfer" ? <FundingManualTransfer onBack={navigation.back} /> : exchangeOpen ? <FundingExchange client={client} open={open} operationId={pendingExchangeId} onBack={() => setExchangeOpen(false)} renderStatus={id_1 => <FundingReceipt onReturnToPurchase={onClose} client={client} operationId={id_1} progress={true} onBack={() => setExchangeOpen(false)} onSignIn={signIn} />} /> : !receiptId && (!currentSession || entryError) ? <FundingMethodPicker client={client} methods={[]} loading={!entryError} onExchange={exchanges.data?.includes("coinbase") ? () => setExchangeOpen(true) : undefined} providerFeedback={entryError ? <FundingEntryFeedback expired={expired} noPaymentTaken={pending.isSuccess && pending.data === null} onSignIn={signIn} onRetry={() => {
            session.refetch({
              cancelRefetch: false
            });
          }} /> : null} /> : receiptId ? <FundingReceipt onReturnToPurchase={onClose} client={client} operationId={receiptId} {...realtime ? {
            realtime
          } : {}} onBack={directPurchase && fundingNavigationManifest.direct_token_purchase.receipt_back === "origin" ? onClose : navigation.back} onSignIn={signIn} /> : currentSession ? <div ref={setCheckoutContainer} /> : null}</div></FundingDialog>;
    };
    $[26] = activeConnectedWalletId;
    $[27] = activeCryptoDepositId;
    $[28] = chrome;
    $[29] = client;
    $[30] = connectedWalletOpen;
    $[31] = cryptoDepositOpen;
    $[32] = currentSession;
    $[33] = directPurchase;
    $[34] = entryError;
    $[35] = exchangeOpen;
    $[36] = exchanges;
    $[37] = expired;
    $[38] = navigation;
    $[39] = onClose;
    $[40] = open;
    $[41] = openProfile;
    $[42] = pending.data;
    $[43] = pending.isSuccess;
    $[44] = pendingExchangeId;
    $[45] = profileOnly;
    $[46] = profileReady;
    $[47] = realtime;
    $[48] = receiptId;
    $[49] = session;
    $[50] = setExchangeOpen;
    $[51] = signIn;
    $[52] = t;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const renderDialog = t17;
  const t18 = chrome.report;
  let t19;
  if ($[54] !== renderDialog) {
    t19 = renderDialog();
    $[54] = renderDialog;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== alternateFlow || $[57] !== authentication || $[58] !== checkoutContainer || $[59] !== client || $[60] !== currentSession || $[61] !== directPurchase || $[62] !== entryError || $[63] !== exchanges || $[64] !== navigation.revision || $[65] !== onClose || $[66] !== open || $[67] !== realtime || $[68] !== receiptId || $[69] !== setExchangeOpen || $[70] !== signIn) {
    t20 = currentSession && !entryError ? <FundingCheckout key={`${currentSession.sessionToken}:${directPurchaseEntryScope(directPurchase)}`} {...directPurchase ? {
      directPurchase
    } : {}} checkoutRevision={navigation.revision} open={open} hostActive={!alternateFlow} client={client} {...realtime ? {
      realtime
    } : {}} sessionToken={currentSession.sessionToken} sessionProofId={currentSession.sessionProofId} authenticationRequested={authentication.authenticationRequested} authenticationBusy={authentication.busy} authenticationError={authentication.error} onAcceptAuthenticationTerms={authentication.acceptTerms} onAuthenticateSandbox={authentication.authenticate} onClose={onClose} onSignIn={signIn} onExchange={exchanges.data?.includes("coinbase") ? () => setExchangeOpen(true) : undefined} onSessionRefresh={reason => {
      if (reason === "disconnect") {
        authentication.reset();
        setSessionGeneration(_temp);
      } else {
        authentication.replace();
      }
    }} render={content => !alternateFlow && !receiptId && checkoutContainer ? createPortal(content, checkoutContainer) : null} /> : null;
    $[56] = alternateFlow;
    $[57] = authentication;
    $[58] = checkoutContainer;
    $[59] = client;
    $[60] = currentSession;
    $[61] = directPurchase;
    $[62] = entryError;
    $[63] = exchanges;
    $[64] = navigation.revision;
    $[65] = onClose;
    $[66] = open;
    $[67] = realtime;
    $[68] = receiptId;
    $[69] = setExchangeOpen;
    $[70] = signIn;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== realtime) {
    t21 = realtime ? {
      realtime
    } : {};
    $[72] = realtime;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  const t22 = open && historyOpen;
  let t23;
  let t24;
  let t25;
  if ($[74] !== navigation) {
    t23 = id_2 => {
      setDismissedCryptoDepositId(null);
      setSelectedCryptoDepositId(id_2);
      navigation.openCryptoDeposit();
    };
    t24 = id_3 => {
      setDismissedConnectedWallet(null);
      setSelectedConnectedWallet({
        id: id_3,
        revision: navigation.revision
      });
      navigation.openConnectedWallet();
    };
    t25 = open_0 => {
      if (open_0) {
        navigation.openHistory();
      } else {
        navigation.closeHistory();
      }
    };
    $[74] = navigation;
    $[75] = t23;
    $[76] = t24;
    $[77] = t25;
  } else {
    t23 = $[75];
    t24 = $[76];
    t25 = $[77];
  }
  let t26;
  if ($[78] !== client || $[79] !== onClose || $[80] !== signIn || $[81] !== t21 || $[82] !== t22 || $[83] !== t23 || $[84] !== t24 || $[85] !== t25) {
    t26 = <FundingChromeContext.Provider value={null}><FundingHistory onReturnToPurchase={onClose} client={client} {...t21} open={t22} onResumeCryptoDeposit={t23} onResumeConnectedWallet={t24} onOpenChange={t25} onSignIn={signIn} /></FundingChromeContext.Provider>;
    $[78] = client;
    $[79] = onClose;
    $[80] = signIn;
    $[81] = t21;
    $[82] = t22;
    $[83] = t23;
    $[84] = t24;
    $[85] = t25;
    $[86] = t26;
  } else {
    t26 = $[86];
  }
  let t27;
  if ($[87] !== chrome.report || $[88] !== t19 || $[89] !== t20 || $[90] !== t26) {
    t27 = <FundingChromeContext.Provider value={t18}>{t19}{t20}{t26}</FundingChromeContext.Provider>;
    $[87] = chrome.report;
    $[88] = t19;
    $[89] = t20;
    $[90] = t26;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  return t27;
}
function _temp(current) {
  return current + 1;
}